import { Download, Trash2, Volume2, Cpu, Image as ImageIcon, Copy, Check, ChevronRight } from 'lucide-react'
import { apiClient } from '../../utils/api'
import type { HistoryItem } from '../../hooks/useHistoryData'

interface HistoryGridViewProps {
  history: HistoryItem[]
  copiedId: string | null
  onDownload: (id: string) => void
  onDelete: (id: string) => void
  onCopyPrompt: (id: string, prompt: string) => void
}

export function HistoryGridView({ history, copiedId, onDownload, onDelete, onCopyPrompt }: HistoryGridViewProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
      {history.map((item) => (
        <div key={item.id} className="bg-[#161b22] rounded-lg overflow-hidden border border-cyan-900/30 hover:border-cyan-500/50 transition-colors group">
          {(item.media_type === 'image' || item.media_type === 'vision') ? (
            <div className="aspect-square relative overflow-hidden bg-[#0d1117]">
              {item.media_type === 'vision' && (
                <div className="absolute top-2 right-2 z-10 bg-purple-600/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium">
                  图像理解
                </div>
              )}
              <img
                src={apiClient.getImageUrl(item.id)}
                alt={item.prompt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJDMTMuMSAyIDE0IDIuOSAxNCA0VjE2QzE0IDE3LjEgMTMuMSAxOCA5LjUgMThIMTUuNUMxNi45IDE4IDE4IDE3LjEgMTggMTZWNFMxNy4xIDMgMTYgM0gxNFoiIGZpbGw9IiM2MzY2ZjEiLz4KPC9zdmc+'
                }}
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center space-x-2">
                <button
                  onClick={() => onDownload(item.id)}
                  className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors"
                  title="下载"
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDelete(item.id)}
                  className="bg-red-500/20 hover:bg-red-500/30 text-red-400 p-2 rounded-lg transition-colors"
                  title="删除"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="aspect-square relative overflow-hidden bg-gradient-to-br from-green-900/50 to-blue-900/50 flex items-center justify-center">
              <div className="text-center">
                <Volume2 className="w-16 h-16 text-green-400 mx-auto mb-4 opacity-75" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center space-x-2">
                  <button
                    onClick={() => window.open(apiClient.getImageUrl(item.id), '_blank')}
                    className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                    title="播放"
                  >
                    <ChevronRight className="w-4 h-4" />
                    <span>播放</span>
                  </button>
                  <button
                    onClick={() => onDownload(item.id)}
                    className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors"
                    title="下载"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(item.id)}
                    className="bg-red-500/20 hover:bg-red-500/30 text-red-400 p-2 rounded-lg transition-colors"
                    title="删除"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="p-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <p className="text-xs text-gray-300 line-clamp-2 flex-1" title={item.prompt}>
                {item.prompt}
              </p>
              <button
                onClick={() => onCopyPrompt(item.id, item.prompt)}
                className="flex-shrink-0 p-1.5 rounded-md hover:bg-white/10 transition-colors group/copy"
                title="复制提示词"
              >
                {copiedId === item.id ? (
                  <Check className="w-3.5 h-3.5 text-green-400" />
                ) : (
                  <Copy className="w-3.5 h-3.5 text-gray-400 group-hover/copy:text-white" />
                )}
              </button>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                {item.media_type === 'image' ? (
                  <Cpu className="w-3 h-3" />
                ) : item.media_type === 'vision' ? (
                  <ImageIcon className="w-3 h-3" />
                ) : (
                  <Volume2 className="w-3 h-3" />
                )}
                <span>{item.model_used.split('/').pop()?.split(':')[0] || 'Unknown'}</span>
              </div>
              <span>{new Date(item.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
